import React, { useState, useEffect, useCallback } from 'react';
import ScrollX from '../components/ScrollX';
import { useYearRange } from '../constants/yearRange';
import { TAX_YEAR } from '../constants/taxYear';
import { supabase } from '../supabase';
import { exportToCsv } from '../utils/exportCsv';
import { formatPhone } from '../utils/format';
import AddConsultantModal from '../components/AddConsultantModal';
import ConsultantContactModal from '../components/ConsultantContactModal';
import ClientDocuments from '../components/ClientDocuments';
import { consultantRoleAbbr, consultantRoleLabel } from '../constants/roles';

export default function ConsultantsPage({ darkMode, staffUser, allClients, onDrillParcel }) {
  // Year selector range ← folder_year_config (Admin → Year Management), one source for every
  // value-year picker in the app. Falls back to 2021-2028 if the config row can't be read.
  const { years } = useYearRange();
  const isDark = darkMode;
  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68',
    th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.04)',
  } : {
    bg: 'var(--bg)', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8',
    th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)',
  };

  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT: { fontSize: '14px', fontWeight: '700', color: c.text },
    th: { padding: '9px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', fontSize: '14px', fontWeight: '500', color: c.text, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}` },
    badge: (col) => ({ background: `rgba(${col},0.12)`, color: `rgb(${col})`, border: `1px solid rgba(${col},0.25)`, borderRadius: '20px', padding: '2px 9px', fontSize: '11px', fontWeight: '600' }),
    btn: { padding: '8px 16px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '7px 14px', background: 'transparent', border: `1px solid ${isDark ? '#374060' : 'var(--line)'}`, borderRadius: '7px', color: c.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    input: { padding: '8px 12px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" },
  };

  // ── State ──────────────────────────────────────────────────────
  const [consultants, setConsultants] = useState([]);
  const [primaryByConsultant, setPrimaryByConsultant] = useState({}); // D2-wire: consultant_id -> primary contact
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState('asc'); // D2: Consultant Name column sort
  const [consultantContacts, setConsultantContacts] = useState([]); // D3: this consultant's contacts (contact-centric)
  const [addOpen, setAddOpen] = useState(false); // D4: Add Consultant modal
  const [contactModal, setContactModal] = useState(null); // D4b: null=closed, {contact:null}=add, {contact:row}=edit
  // Assignments are per value year (start-year rows). Without a selector this page would only ever show
  // the current year's resolution, and prior-year engagements would look like they never existed.
  const [asgYear, setAsgYear] = useState(String(TAX_YEAR));
  const [contactSearch, setContactSearch] = useState(''); // D4b: All Contacts table search
  const [editConsultant, setEditConsultant] = useState(null); // edit-consultant modal (null=closed, row=editing)
  const [showRetiredConsultants, setShowRetiredConsultants] = useState(false); // retire filter (consultants list)
  const [drill, setDrill] = useState(null);           // consultant
  const [drillProperty, setDrillProperty] = useState(null); // property group
  const [assignments, setAssignments] = useState([]);
  // ⛔ A FAILED READ IS NOT AN EMPTY RESULT. This panel used to console.warn the error and fall through to
  // "No property assignments found" — so a 400 (a missing FK breaking the embed) rendered as "this firm
  // has no parcels", which is a confident statement of something untrue. Same shape as the RLS 0-row and
  // truncation classes: it looks fine and isn't. Non-empty = show the failure instead of the empty state.
  const [assignmentsErr, setAssignmentsErr] = useState('');
  const [tokens, setTokens] = useState([]);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [newTokenUrl, setNewTokenUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);

  const loadPrimaryMap = useCallback(() => {
    supabase.from('consultant_contact_roles')
      .select('consultant_id, consultant_contacts(first_name, last_name, email, phone)')
      .eq('role', 'primary')
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(r => { if (r.consultant_contacts) map[r.consultant_id] = r.consultant_contacts; });
        setPrimaryByConsultant(map);
      });
  }, []);

  useEffect(() => {
    supabase.from('consultants').select('*').order('company').then(({ data }) => {
      setConsultants(data || []);
      setLoading(false);
    });
    loadPrimaryMap(); // D2-wire: batched primary-contact map (also refreshed on contact edits)
  }, []);

  const loadConsultantContacts = useCallback(async (consultantId) => {
    const { data } = await supabase.from('consultant_contacts')
      .select('id, first_name, last_name, title, email, phone, consultant_contact_roles(role)')
      .eq('consultant_id', consultantId);
    setConsultantContacts(data || []);
  }, []);

  const loadDrill = useCallback(async (consultant) => {
    setDrill(consultant);
    setDrillProperty(null);
    setNewTokenUrl(null);
    setConsultantContacts([]); // D3: clear so a previous consultant's contacts don't flash before the fetch resolves
    // Assignments are PARCEL-grained and per value year, keyed on consultant_id (was: a TEXT match on
    // consultant_name = consultants.company, which a firm rename silently orphaned). Everything the table
    // used to store as denormalised text — property name, client name, state, account number, assessor,
    // RE/PP — now comes through the FKs.
    //
    // ⛔ START-YEAR SEMANTICS: an assignment stands from its value_year until superseded, so this asks for
    // every row AT OR BEFORE the selected year and keeps the latest per parcel. An inline
    // .eq('value_year', y) would show nothing for every year between changes.
    const { data: asgnRows, error: asgnErr } = await supabase.from('consultant_assignments')
      .select('*, parcel:parcel_id (id, account_number, assessor, state, re_pp, property_id, property:property_id (id, name, master_property_id, client_id, client:client_id (id, name)))')
      .eq('consultant_id', consultant.id)
      .lte('value_year', Number(asgYear))
      .order('value_year', { ascending: false });
    if (asgnErr) {
      console.warn('[ConsultantsPage] assignments read failed:', asgnErr.message);
      setAssignmentsErr(asgnErr.message || 'the assignments query failed');
      setAssignments([]);
      setTokens([]); setUploadHistory([]);
      await loadConsultantContacts(consultant.id);
      return;   // do NOT fall through: an empty list here would read as "no assignments"
    }
    setAssignmentsErr('');
    const mine = new Map();
    (asgnRows || []).forEach(r => { if (!mine.has(r.parcel_id)) mine.set(r.parcel_id, r); });

    // ⛔ A row of OURS being the latest of OURS does not make it the parcel's CURRENT assignment: the
    // parcel may have moved to another firm — or been ended with a tombstone — in a later year. Filtering
    // only by consultant_id would keep showing this firm on parcels it no longer holds. So resolve each
    // parcel's true latest row (any firm, at or before the year) and keep only the ones that are ours.
    const parcelIds = [...mine.keys()];
    if (parcelIds.length) {
      const { data: allRows, error: allErr } = await supabase.from('consultant_assignments')
        .select('parcel_id, consultant_id, value_year')
        .in('parcel_id', parcelIds)
        .lte('value_year', Number(asgYear))
        .order('value_year', { ascending: false });
      if (allErr) {
        // This check REMOVES parcels that moved to another firm. If it fails we cannot tell which are
        // still ours, and showing the unfiltered set would assert assignments this firm may have lost.
        console.warn('[ConsultantsPage] current-holder check failed:', allErr.message);
        setAssignmentsErr(allErr.message || 'could not confirm which parcels are still assigned to this firm');
        setAssignments([]);
        return;
      }
      const holder = new Map();
      (allRows || []).forEach(r => { if (!holder.has(r.parcel_id)) holder.set(r.parcel_id, r.consultant_id); });
      parcelIds.forEach(pid => { if (holder.get(pid) !== consultant.id) mine.delete(pid); });
    }
    setAssignments([...mine.values()]);
    const { data: toks } = await supabase.from('upload_tokens')
      .select('*').eq('consultant_firm', consultant.company).order('created_at', { ascending: false });
    setTokens(toks || []);
    const { data: uploads } = await supabase.from('file_exchange')
      .select('*').eq('uploaded_by_name', consultant.company)
      .order('created_at', { ascending: false }).limit(50);
    setUploadHistory(uploads || []);
    await loadConsultantContacts(consultant.id);
  }, [asgYear]);

  // Group assignments by property
  const groupedByProperty = React.useMemo(() => {
    const map = {};
    assignments.forEach(a => {
      // Key on the property ID, not a name string. The old key was
      // `master_property_id||property_name||client_name` off denormalised columns, so a renamed property
      // split into two groups. Names now come from the join and are display-only.
      const prop = a.parcel?.property || null;
      const key = prop?.id || '_unknown';
      if (!map[key]) map[key] = {
        propertyId: prop?.id || null,
        master_property_id: prop?.master_property_id || null,
        property_name: prop?.name || 'Unknown property',
        client_name: prop?.client?.name || '—',
        assessor: a.parcel?.assessor || null,
        state: a.parcel?.state || null,
        parcels: [],
      };
      map[key].parcels.push(a);
    });
    return Object.values(map).sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
  }, [assignments]);

  async function generateConsultantToken(consultant) {
    setGeneratingToken(true);
    const expires = new Date();
    expires.setDate(expires.getDate() + 90);
    const { data, error } = await supabase.from('upload_tokens').insert({
      client_id: null, client_name: consultant.company,
      consultant_firm: consultant.company, uploader_type: 'consultant',
      created_by: staffUser?.name || 'NTRG Staff',
      expires_at: expires.toISOString(), active: true,
    }).select().single();
    if (!error && data) {
      setNewTokenUrl(`${window.location.origin}/upload/${data.token}`);
      setTokens(prev => [data, ...prev]);
    }
    setGeneratingToken(false);
  }

  async function deactivateToken(tokenId) {
    await supabase.from('upload_tokens').update({ active: false }).eq('id', tokenId);
    setTokens(prev => prev.map(t => t.id === tokenId ? { ...t, active: false } : t));
  }

  function copyLink(url) {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const filtered = consultants.filter(con => {
    if (!showRetiredConsultants && con.status === 'Retired') return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const p = primaryByConsultant[con.id];
    const primaryName = p ? (p.first_name + ' ' + p.last_name) : '';
    return con.company.toLowerCase().includes(q)
      || (con.state || '').toLowerCase().includes(q)
      || (con.city || '').toLowerCase().includes(q)
      || primaryName.toLowerCase().includes(q)
      || (p && p.email ? p.email.toLowerCase() : '').includes(q);
  });
  const displayed = [...filtered].sort((a, b) =>
    sortDir === 'asc'
      ? (a.company || '').localeCompare(b.company || '')
      : (b.company || '').localeCompare(a.company || ''));

  // ⛔ THE EDIT MODAL IS OPENED FROM THE CONSULTANT'S OWN PAGE (✏️ Edit), SO IT MUST RENDER THERE. It used to sit in
  // the list view's return only: Edit set editConsultant on a screen that never drew it, and the form appeared only
  // when something sent the user back to the list — the "Edit, then the browser's Back button" workaround (2026-11-03).
  const editModal = editConsultant && (
    <AddConsultantModal
      isDark={isDark}
      consultant={editConsultant}
      onClose={() => setEditConsultant(null)}
      onSuccess={(updated) => {
        setEditConsultant(null);
        supabase.from('consultants').select('*').order('company').then(({ data }) => {
          const list = data || [];
          setConsultants(list);
          const fresh = list.find(x => x.id === updated.id);
          if (fresh) setDrill(fresh);
        });
      }}
    />
  );

  // ── PARCEL DRILL VIEW ──────────────────────────────────────────
  if (drill && drillProperty) {
    return (
      <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '13px', color: c.muted }}>
          <span style={{ cursor: 'pointer', color: 'var(--red)' }} onClick={() => setDrill(null)}>← Consultants</span>
          <span>›</span>
          <span style={{ cursor: 'pointer', color: 'var(--red)' }} onClick={() => setDrillProperty(null)}>{drill.company}</span>
          <span>›</span>
          <span style={{ color: c.text, fontWeight: '600' }}>{drillProperty.property_name}</span>
        </div>

        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '22px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>{drillProperty.property_name}</div>
        <div style={{ fontSize: '13px', color: c.muted, marginBottom: '22px' }}>{drillProperty.client_name} · {drillProperty.assessor} · {drillProperty.state}</div>

        <div style={s.card}>
          <div style={s.cardH}>
            <div style={s.cardT}>📋 Parcels</div>
            <span style={{ color: 'var(--charcoal)' }}>{drillProperty.parcels.length} parcels</span>
          </div>
          <ScrollX style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={s.th}>Account #</th>
                  <th style={s.th}>RE/PP</th>
                  <th style={s.th}>Assessor</th>
                  <th style={s.th}>State</th>
                  <th style={s.th} title="The value year this assignment started; it stands until superseded">Since</th>
                </tr>
              </thead>
              <tbody>
                {drillProperty.parcels.map((p, i) => (
                  <tr key={i}
                    onClick={async () => {
                      if (!onDrillParcel) return;
                      // By id, via the FK. Was .eq('account_number',…).eq('assessor',…).single(), which
                      // THROWS when two parcels share that pair — this dataset has had duplicate account
                      // numbers (12 at last count), so the old lookup could hard-fail on a click.
                      const { data, error } = await supabase.from('parcels').select('*').eq('id', p.parcel_id).maybeSingle();
                      if (error) { console.warn('[ConsultantsPage] parcel open failed:', error.message); return; }
                      if (data) onDrillParcel(data);
                    }}
                    style={{ cursor: onDrillParcel ? 'pointer' : 'default' }}
                    onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...s.td, fontFamily: "'DM Mono',monospace", fontSize: '11px', fontWeight: '600', color: onDrillParcel ? 'var(--red)' : c.text }}>{p.parcel?.account_number || '—'}</td>
                    <td style={s.td}><span style={{ fontSize: '14px', color: 'var(--charcoal)' }}>{p.parcel?.re_pp === 'Real Estate' ? 'RE' : 'PP'}</span></td>
                    <td style={s.td}>{p.parcel?.assessor || '—'}</td>
                    <td style={s.td}>{p.parcel?.state || '—'}</td>
                    <td style={s.td}><span style={{ ...s.td, fontSize:'11px', color:'var(--charcoal)' }}>{p.value_year}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </div>
      </div>
    );
  }

  // ── CONSULTANT DRILL VIEW ──────────────────────────────────────
  if (drill) {
    const displayedContacts = consultantContacts.filter(ct => {
      if (!contactSearch) return true;
      const q = contactSearch.toLowerCase();
      return (ct.first_name + ' ' + ct.last_name).toLowerCase().includes(q)
        || (ct.title || '').toLowerCase().includes(q)
        || (ct.email || '').toLowerCase().includes(q)
        || (ct.phone || '').toLowerCase().includes(q);
    });
    return (
      <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
        {editModal}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '13px', color: c.muted }}>
          <span style={{ cursor: 'pointer', color: 'var(--red)' }} onClick={() => setDrill(null)}>← Consultants</span>
          <span>›</span>
          <span style={{ color: c.text, fontWeight: '600' }}>{drill.company}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text }}>{drill.company}</div>
          <button onClick={() => setEditConsultant(drill)} style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>✏️ Edit</button>
          <button onClick={async () => {
            const retiring = drill.status !== 'Retired';
            const ok = window.confirm(retiring ? `Retire ${drill.company}? They'll be hidden from the consultants list.` : `Unretire ${drill.company}?`);
            if (!ok) return;
            const newStatus = retiring ? 'Retired' : 'Active';
            const { error } = await supabase.from('consultants').update({ status: newStatus }).eq('id', drill.id);
            if (error) { window.alert('Update failed: ' + error.message); return; }
            setConsultants(prev => prev.map(x => x.id === drill.id ? { ...x, status: newStatus } : x));
            if (retiring) setDrill(null); else setDrill({ ...drill, status: newStatus });
          }} style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>{drill.status === 'Retired' ? 'Unretire Consultant' : 'Retire Consultant'}</button>
        </div>
        <div style={{ fontSize: '13px', color: c.muted, marginBottom: '22px' }}>
          {[drill.city, drill.state].filter(Boolean).join(', ')}
          {drill.email && <> · <a href={`mailto:${drill.email}`} style={{ color: 'var(--red)', textDecoration: 'none' }}>{drill.email}</a></>}
        </div>

        {contactModal && (
          <ConsultantContactModal
            isDark={isDark}
            consultant={drill}
            contact={contactModal.contact}
            existingContacts={consultantContacts}
            onClose={() => setContactModal(null)}
            onSuccess={() => { setContactModal(null); loadConsultantContacts(drill.id); loadPrimaryMap(); }}
          />
        )}

        {/* Consultant Contacts + Services + Address (D3) */}
        <div style={s.card}>
          <div style={{ display: 'flex', gap: '24px', padding: '14px 18px' }}>
            {/* LEFT — Consultant Contacts */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '.08em', textTransform: 'uppercase', color: c.text }}>👤 Consultant Contacts</div>
                <button onClick={() => setContactModal({ contact: null })} style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px' }}>+ Add</button>
              </div>
              {(() => {
                const hdr = { fontSize: '10px', fontWeight: '700', letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted, marginBottom: '6px' };
                const na = { fontSize: '12px', color: c.muted, fontStyle: 'italic' };
                const slot = (label, role) => {
                  const ct = consultantContacts.find(x => (x.consultant_contact_roles || []).some(r => r.role === role));
                  return (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={hdr}>{label}</div>
                      {ct ? (
                        <div style={{ fontSize: '13px', color: c.text }}>
                          {ct.first_name} {ct.last_name}
                          {ct.title && <span style={{ color: c.muted }}> · {ct.title}</span>}
                          {ct.email && <span style={{ color: c.muted }}> · {ct.email}</span>}
                          {ct.phone && <span style={{ color: c.muted }}> · {formatPhone(ct.phone)}</span>}
                        </div>
                      ) : <div style={na}>Not assigned</div>}
                    </div>
                  );
                };
                return (<>{slot('Primary Contact', 'primary')}{slot('Invoice Contact', 'invoice')}</>);
              })()}
            </div>
            {/* MIDDLE — Services + contract */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '.08em', textTransform: 'uppercase', color: c.text, marginBottom: '12px' }}>Services</div>
              {(drill.services && drill.services.length > 0)
                ? <div style={{ fontSize: '12px', color: c.text, lineHeight: 1.6 }}>{drill.services.map((sv, i) => <div key={i}>{sv}</div>)}</div>
                : <div style={{ fontSize: '12px', color: c.muted }}>—</div>}
              <div style={{ fontSize: '12px', color: c.sub, marginTop: '12px' }}>Contract expires: {drill.contract_expiration_date || '—'}</div>
              <div style={{ fontSize: '12px', color: c.sub, marginTop: '4px' }}>Auto-renews: {drill.auto_renew ? 'Yes' : 'No'}</div>
            </div>
            {/* RIGHT — Address */}
            <div style={{ width: '220px', borderLeft: `1px solid ${c.border}`, paddingLeft: '18px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '.08em', textTransform: 'uppercase', color: c.text, marginBottom: '12px' }}>Address</div>
              {(() => {
                const { address_line1, address_line2, city, state, postal_code } = drill;
                const cityLine = [city, [state, postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                if (!(address_line1 || address_line2 || city || state || postal_code)) return <div style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic' }}>—</div>;
                return (
                  <div style={{ fontSize: '12px', color: c.text, lineHeight: 1.5 }}>
                    {address_line1 && <div>{address_line1}</div>}
                    {address_line2 && <div>{address_line2}</div>}
                    {cityLine && <div>{cityLine}</div>}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* 📂 Documents filed against this firm (consultant-level folders) — staff only. The shared card is styled with
            StaffPortal's style names, mapped here onto this page's. */}
        <ClientDocuments consultantId={drill.id} consultantName={drill.company}
          c={{ ...c, activeNav: isDark ? 'rgba(180,0,0,0.1)' : 'rgba(180,0,0,0.15)' }}
          s={{ ...s, cardHeader: s.cardH, cardTitle: s.cardT, btnSm: { ...s.btnGhost, padding: '4px 10px' } }} />

        {/* All Contacts (D4b) */}
        <div style={s.card}>
          <div style={s.cardH}><div style={s.cardT}>All Contacts</div></div>
          <div style={{ padding: '12px 12px 0' }}>
            <input style={{ ...s.input, width: '100%', boxSizing: 'border-box' }} placeholder="Search contacts by name, title, email, or phone..." value={contactSearch} onChange={e => setContactSearch(e.target.value)} />
          </div>
          <ScrollX style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Title</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Phone</th>
                <th style={s.th}>Roles</th>
              </tr></thead>
              <tbody>
                {displayedContacts.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: c.muted }}>{consultantContacts.length === 0 ? 'No contacts yet.' : 'No contacts match your search.'}</td></tr>
                ) : displayedContacts.map(ct => (
                  <tr key={ct.id} onClick={() => setContactModal({ contact: ct })} style={{ cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>{ct.first_name} {ct.last_name}</td>
                    <td style={s.td}>{ct.title || '—'}</td>
                    <td style={s.td}>{ct.email || '—'}</td>
                    <td style={s.td}>{ct.phone ? formatPhone(ct.phone) : '—'}</td>
                    <td style={s.td}>
                      {(ct.consultant_contact_roles || []).length === 0
                        ? <span style={{ color: c.muted }}>—</span>
                        : <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {(ct.consultant_contact_roles || []).map(r => <span key={r.role} title={consultantRoleLabel(r.role)} style={s.badge('96,165,250')}>{consultantRoleAbbr(r.role)}</span>)}
                          </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </div>

        {/* Properties */}
        <div style={s.card}>
          <div style={s.cardH}>
            <div style={s.cardT}>🏢 Properties</div>
            <span style={{ color: 'var(--charcoal)' }}>{groupedByProperty.length} properties · {assignments.length} parcels</span>
            {/* Assignments are per value year and stand until superseded, so the page has to say WHICH year
                it is resolving. Without this it silently shows only the current year's answer. */}
            <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--charcoal)' }}>
              Value year{' '}
              <select value={asgYear} onChange={e => setAsgYear(e.target.value)} style={{ ...s.select, width: 'auto', display: 'inline-block', padding: '2px 6px', fontSize: '12px' }}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </span>
          </div>
          {assignmentsErr ? (
            <div style={{ padding: '20px 24px', fontSize: '13px', color: '#b3282d' }}>
              ⚠ Couldn't load assignments — {assignmentsErr}
              <div style={{ fontSize: '11px', color: c.muted, marginTop: '4px' }}>
                This is a failed read, not an empty list. The counts above are not this firm's real workload.
              </div>
            </div>
          ) : groupedByProperty.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: c.muted }}>No property assignments found</div>
          ) : (
            <ScrollX style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={s.th}>Property</th>
                  <th style={s.th}>Client</th>
                  <th style={s.th}>Assessor</th>
                  <th style={s.th}>State</th>
                  <th style={{ ...s.th, textAlign:'center' }}>Parcels</th>
                </tr></thead>
                <tbody>
                  {groupedByProperty.map((prop, i) => (
                    <tr key={i} onClick={() => setDrillProperty(prop)} style={{ cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>{prop.property_name || '—'}</td>
                      <td style={s.td}>{prop.client_name || '—'}</td>
                      <td style={s.td}>{prop.assessor || '—'}</td>
                      <td style={s.td}>{prop.state || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}><span style={{ color: 'var(--charcoal)' }}>{prop.parcels.length}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>

        {/* Upload Links */}
        <div style={{ ...s.card, border: '1px solid rgba(180,0,0,0.4)' }}>
          <div style={s.cardH}>
            <div style={s.cardT}>🔗 Upload Links</div>
            <button style={s.btn} onClick={() => generateConsultantToken(drill)} disabled={generatingToken}>
              {generatingToken ? '⏳ Generating...' : '+ Generate New Link'}
            </button>
          </div>
          <div style={{ padding: '14px 18px' }}>
            {newTokenUrl && (
              <div style={{ marginBottom: '16px', padding: '12px 14px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#34d399', marginBottom: '6px' }}>✓ New link generated</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1, fontSize: '11px', color: c.muted, fontFamily: "'DM Mono',monospace", wordBreak: 'break-all', padding: '6px 8px', background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--line-soft)', borderRadius: '5px' }}>{newTokenUrl}</div>
                  <button onClick={() => copyLink(newTokenUrl)} style={{ ...s.btn, whiteSpace: 'nowrap', background: copied ? 'rgba(52,211,153,0.15)' : 'linear-gradient(135deg,#B40000,#9C0000)', border: copied ? '1px solid #34d399' : 'none', color: copied ? '#34d399' : 'var(--ink)' }}>
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>
            )}
            {tokens.length === 0 && !newTokenUrl && (
              <div style={{ fontSize: '13px', color: c.muted, padding: '8px 0' }}>No upload links yet. Click "Generate New Link" to create one.</div>
            )}
            {tokens.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={s.th}>Created</th>
                  <th style={s.th}>Expires</th>
                  <th style={s.th}>Files</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {tokens.map(tok => {
                    const expired = new Date(tok.expires_at) < new Date();
                    const url = `${window.location.origin}/upload/${tok.token}`;
                    return (
                      <tr key={tok.id} onMouseEnter={e => e.currentTarget.style.background = c.rowHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ ...s.td, fontSize: '11px' }}>{new Date(tok.created_at).toLocaleDateString()}</td>
                        <td style={{ ...s.td, fontSize: '11px', color: expired ? '#f87171' : c.sub }}>{new Date(tok.expires_at).toLocaleDateString()}</td>
                        <td style={{ ...s.td, textAlign: 'center' }}>{tok.files_received || 0}</td>
                        <td style={s.td}>
                          {!tok.active ? <span style={s.badge('248,113,113')}>Deactivated</span>
                            : expired ? <span style={s.badge('251,191,36')}>Expired</span>
                            : <span style={s.badge('52,211,153')}>Active</span>}
                        </td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {tok.active && !expired && <button onClick={() => copyLink(url)} style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px' }}>📋 Copy</button>}
                            {tok.active && <button onClick={() => deactivateToken(tok.id)} style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px', color: '#f87171', borderColor: 'rgba(248,113,113,0.3)' }}>Deactivate</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Upload History */}
        {uploadHistory.length > 0 && (
          <div style={s.card}>
            <div style={s.cardH}>
              <div style={s.cardT}>📁 Upload History</div>
              <span style={s.badge('180,0,0')}>{uploadHistory.length} files</span>
            </div>
            <ScrollX style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr>
                  <th style={s.th}>File Name</th>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Size</th>
                  <th style={s.th}>Comment</th>
                </tr></thead>
                <tbody>
                  {uploadHistory.map(f => (
                    <tr key={f.id} onMouseEnter={e => e.currentTarget.style.background = c.rowHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...s.td, fontWeight: '600', color: c.text }}>{f.file_name}</td>
                      <td style={{ ...s.td, fontSize: '11px' }}>{new Date(f.created_at).toLocaleDateString()}</td>
                      <td style={{ ...s.td, fontSize: '11px' }}>{f.file_size_bytes ? `${(f.file_size_bytes/1024).toFixed(0)} KB` : '—'}</td>
                      <td style={{ ...s.td, fontSize: '11px', color: f.uploader_comment ? c.text : c.muted, fontStyle: f.uploader_comment ? 'normal' : 'italic' }}>
                        {f.uploader_comment || 'No comment'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>Consultants</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '22px' }}>{consultants.length} consultants in WigleyHub</div>

      {addOpen && (
        <AddConsultantModal
          isDark={isDark}
          onClose={() => setAddOpen(false)}
          onSuccess={(con) => {
            setAddOpen(false);
            supabase.from('consultants').select('*').order('company').then(({ data }) => setConsultants(data || []));
            loadDrill(con);
            loadPrimaryMap();
          }}
        />
      )}

      <div style={s.card}>
        <div style={s.cardH}>
          <div style={s.cardT}>All Consultants</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', color: c.muted }}>{filtered.length} of {consultants.length}</div>
            <button onClick={() => setAddOpen(true)} style={s.btn}>+ Add Consultant</button>
            <button onClick={() => {
              const rows = displayed.map(con => { const p = primaryByConsultant[con.id]; return { company: con.company, state: con.state || '', primary: p ? p.first_name + ' ' + p.last_name : '', email: p && p.email ? p.email : '', phone: p && p.phone ? formatPhone(p.phone) : '' }; });
              const d = new Date();
              const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              exportToCsv(`consultants-${stamp}.csv`, [
                { key: 'company', label: 'Consultant Name' },
                { key: 'state',   label: 'State' },
                { key: 'primary', label: 'Primary Contact' },
                { key: 'email',   label: 'Primary Email' },
                { key: 'phone',   label: 'Phone #' },
              ], rows);
            }} style={s.btnGhost}>Export CSV</button>
          </div>
        </div>
        <div style={{ padding: '12px 12px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input placeholder="Search consultants by name or primary contact..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...s.input, flex: 1, boxSizing: 'border-box' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.muted, cursor: 'pointer', whiteSpace: 'nowrap', padding: '0 8px' }}>
            <input type="checkbox" checked={showRetiredConsultants} onChange={e => setShowRetiredConsultants(e.target.checked)} style={{ cursor: 'pointer' }} />
            Show retired
          </label>
        </div>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: c.muted }}>⏳ Loading...</div>
        ) : (
          <ScrollX style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...s.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>Consultant Name {sortDir === 'asc' ? '▲' : '▼'}</th>
                <th style={s.th}>State</th>
                <th style={s.th}>Primary Contact</th>
                <th style={s.th}>Primary Email</th>
                <th style={s.th}>Phone #</th>
              </tr></thead>
              <tbody>
                {displayed.map(con => {
                  const p = primaryByConsultant[con.id];
                  return (
                    <tr key={con.id} onClick={() => loadDrill(con)} style={{ cursor: 'pointer', opacity: con.status === 'Retired' ? 0.6 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>{con.company}</td>
                      <td style={s.td}>{con.state || '—'}</td>
                      <td style={s.td}>{p ? p.first_name + ' ' + p.last_name : '—'}</td>
                      <td style={s.td}>{p && p.email ? p.email : '—'}</td>
                      <td style={s.td}>{p && p.phone ? formatPhone(p.phone) : '—'}</td>
                    </tr>
                  );
                })}
                {displayed.length === 0 && <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: c.muted }}>No consultants match</td></tr>}
              </tbody>
            </table>
          </ScrollX>
        )}
      </div>
    </div>
  );
}
